/**
 * ============================================================================
 * 문의 수정 페이지
 * ============================================================================
 * 
 * 문의 수정 페이지 컴포넌트
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 기능:
 * - 문의 수정 폼
 * - 첨부파일 선택 및 업로드
 * - 수정 완료 시 티켓 업데이트
 * 
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import SubLayout from '../../components/layout/SubLayout';
import { useFrontAuthStore } from '../../auth/store/authStore';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import { attachmentService } from '../../services/attachmentService';
import { updateTicket, getTicket, uploadFile } from '../../services/inquiryService';
import { TicketUpdateRequest, TicketCategory, TicketAttachment } from '../../types/inquiry';

const Container = styled.div`
  width: 100%;
  max-width: calc(100vw - 260px);
  padding: ${props => props.theme.spacing['3xl']};
  background: var(--bg-secondary);
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSize['2xl']};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: var(--text-primary, #333);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.book.color6b};
`;

const FilterSelect = styled.select`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border.de};
  border-radius: ${props => props.theme.borderRadius.md};
  background: var(--bg-primary, #fff);
  color: var(--text-primary, #333);
  font-size: ${props => props.theme.fontSize.base};
  cursor: pointer;
  height: 44px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.book.color2b};
  }
`;

const FileInput = styled.input`
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border.de};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.base};
  height: 44px;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  margin-top: ${props => props.theme.spacing.sm};
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm};
  background: var(--bg-secondary, #f5f5f5);
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 14px;
`;

const AttachmentPreview = styled.div`
  margin-top: ${props => props.theme.spacing.md};
`;

const AttachmentImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.sm};
  cursor: pointer;
  border: 1px solid var(--border-color, #e0e0e0);
`;

const ExistingAttachmentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm};
  background: var(--bg-secondary, #f5f5f5);
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 14px;
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const AttachmentLink = styled.a`
  color: var(--primary-color, #3b82f6);
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const AttachmentInfo = styled.div`
  font-size: 12px;
  color: var(--text-secondary, #666);
  margin-top: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  margin-top: ${props => props.theme.spacing.lg};
`;

const InquiryBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  position: relative;
  margin-top: ${props => props.theme.spacing.xl};
`;

const MENU_ITEMS = [
  {
    key: 'profile',
    label: '프로필 수정',
    icon: '/images/book/icon_user.svg',
    children: [
      { key: 'settings-general', label: '기본 정보 설정', path: '/profile' },
      { key: 'settings-school', label: '학적 정보 설정', path: '/profile' },
    ],
  },
  { key: 'inquiry', label: '문의하기', icon: '/images/book/icon_inquiry.svg', path: '/inquiry', active: true },
  { key: 'logout', label: '로그아웃' },
];

const InquiryEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useFrontAuthStore();
  const { ticketNo } = useParams<{ ticketNo: string }>();
  const [loading, setLoading] = useState(false);
  const [loadingTicket, setLoadingTicket] = useState(true);
  const [formData, setFormData] = useState<TicketUpdateRequest>({
    title: '',
    content: '',
    category: 'QUESTION',
    priority: 'NORMAL',
    file_uuids: [],
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<TicketAttachment[]>([]);

  // 티켓 정보 로드
  useEffect(() => {
    const loadTicket = async () => {
      if (!ticketNo) {
        alert('티켓 번호가 없습니다.');
        navigate('/inquiry');
        return;
      }

      try {
        setLoadingTicket(true);
        const ticket = await getTicket(parseInt(ticketNo, 10));
        // 기존 첨부파일 저장
        const existingAttachments = ticket.attachments || [];
        setExistingAttachments(existingAttachments);
        // 기존 파일 UUID를 formData에 포함
        const existingFileUuids = existingAttachments.map(att => att.file_uuid);
        setFormData({
          title: ticket.title,
          content: ticket.content,
          category: ticket.category,
          priority: ticket.priority,
          file_uuids: existingFileUuids,
        });
      } catch (error) {
        // console.error('티켓 로드 실패:', error);
        alert('티켓을 불러오는데 실패했습니다.');
        navigate('/inquiry');
      } finally {
        setLoadingTicket(false);
      }
    };

    loadTicket();
  }, [ticketNo, navigate]);

  // 티켓 수정
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!ticketNo) return;

    if (!formData.title?.trim() || !formData.content?.trim()) {
      alert('제목과 내용을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      // 파일이 있으면 먼저 업로드
      let fileUuids: string[] = [];
      if (selectedFiles.length > 0) {
        try {
          const uploadPromises = selectedFiles.map(file => uploadFile(file));
          fileUuids = await Promise.all(uploadPromises);
        } catch (error) {
          // console.error('파일 업로드 실패:', error);
          alert('파일 업로드에 실패했습니다.');
          setLoading(false);
          return;
        }
      }
      
      // 티켓 수정 (기존 파일 UUID + 새로 업로드한 파일 UUID)
      await updateTicket(parseInt(ticketNo, 10), {
        ...formData,
        file_uuids: [...(formData.file_uuids || []), ...fileUuids],
      });
      
      // 목록 페이지로 이동
      navigate('/inquiry');
    } catch (error) {
      // console.error('문의 수정 실패:', error);
      alert('문의를 수정하는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 파일 선택
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  // 파일 삭제
  const handleFileRemove = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleMenuClick = useCallback(async (key: string) => {
    if (key === 'logout') {
      try { await logout(); } catch (e) { /* ignore */ }
    }
  }, [logout]);

  if (loadingTicket) {
    return (
      <SubLayout menuItems={MENU_ITEMS} activeMenu="inquiry" onMenuClick={handleMenuClick}>
        <Container>
          <div style={{ textAlign: 'center', padding: '2rem' }}>로딩 중...</div>
        </Container>
      </SubLayout>
    );
  }

  return (
    <SubLayout menuItems={MENU_ITEMS} activeMenu="inquiry" onMenuClick={handleMenuClick}>
      <Container>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>분류</Label>
            <FilterSelect
              value={formData.category}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setFormData({ ...formData, category: e.target.value as TicketCategory })
              }
            >
              <option value="QUESTION">문의</option>
              <option value="BUG">버그</option>
              <option value="FEATURE">기능</option>
              <option value="ETC">기타</option>
            </FilterSelect>
          </FormGroup>

          <FormGroup>
            <Label>제목</Label>
            <Input
              placeholder="제목을 입력하세요"
              value={formData.title || ''}
              onChange={(value) => setFormData({ ...formData, title: value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>내용</Label>
            <Textarea
              placeholder="내용을 입력하세요"
              value={formData.content || ''}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={15}
            />
          </FormGroup>

          {/* 첨부파일 숨김 처리
          <FormGroup>
            <Label>첨부파일</Label>

            {existingAttachments.length > 0 && (
              <AttachmentPreview>
                <div style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 600 }}>
                  기존 첨부파일 ({existingAttachments.length}개)
                </div>
                {existingAttachments.map((attachment: TicketAttachment) => {
                  const isImage = attachment.mime_type?.startsWith('image/');
                  const fileUrl = attachmentService.getFullFileUrl(attachment.file_uuid);

                  return (
                    <ExistingAttachmentItem key={attachment.attachment_no}>
                      <div style={{ flex: 1 }}>
                        {isImage && (
                          <AttachmentImage
                            src={fileUrl}
                            alt={attachment.original_name}
                            onClick={() => window.open(fileUrl, '_blank')}
                          />
                        )}
                        <AttachmentLink
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(fileUrl, '_blank');
                          }}
                        >
                          {attachment.original_name}
                        </AttachmentLink>
                        <AttachmentInfo>
                          {attachmentService.formatFileSize(attachment.file_size)}
                        </AttachmentInfo>
                      </div>
                    </ExistingAttachmentItem>
                  );
                })}
              </AttachmentPreview>
            )}

            <div style={{ marginTop: existingAttachments.length > 0 ? '16px' : '0' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                새 파일 추가
              </div>
              <FileInput
                type="file"
                multiple
                onChange={handleFileSelect}
              />
              {selectedFiles.length > 0 && (
                <FileList>
                  {selectedFiles.map((file, idx) => (
                    <FileItem key={idx}>
                      <span>{file.name} ({(file.size / 1024).toFixed(2)} KB)</span>
                      <button
                        type="button"
                        onClick={() => handleFileRemove(idx)}
                        disabled={loading}
                      >
                        삭제
                      </button>
                    </FileItem>
                  ))}
                </FileList>
              )}
            </div>
          </FormGroup>
          */}

          <ButtonGroup>
            <Button
              type="button"
              size="lg"
              width={180}                
              variant="secondary"
              onClick={() => navigate('/inquiry')}
            >
              취소
            </Button>
            <Button
              type="submit"
              size="lg"
              width={180}                
              disabled={loading}
            >
              {loading ? '수정 중...' : '수정 완료'}
            </Button>
          </ButtonGroup>
        </Form>
      </Container>
      <InquiryBottom>
        <Button
          variant="secondary"
          size="lg"
          width={130}                
          onClick={() => navigate(-1)}
        >
          뒤로가기
        </Button>
      </InquiryBottom>
    </SubLayout>
  );
};

export default InquiryEditPage;
